import { css, useTheme } from '@emotion/react';
import * as Dialog from '@radix-ui/react-dialog';
import { useState } from 'react';

import { Spinner } from '@/components/ui/spinner';
import { fontSizes, radii, shadows, spacing } from '@/theme/tokens';

interface ChangePlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (planId: string) => Promise<void>;
  plans: { id: string; name: string; displayName: string }[];
  currentPlanName: string | null;
  userEmail: string;
}

export function ChangePlanModal({
  isOpen,
  onClose,
  onSave,
  plans,
  currentPlanName,
  userEmail,
}: ChangePlanModalProps) {
  const theme = useTheme();
  const currentPlan = plans.find((p) => p.name === currentPlanName);
  const [selectedPlanId, setSelectedPlanId] = useState(currentPlan?.id ?? '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSave() {
    if (!selectedPlanId) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(selectedPlanId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update plan');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog.Root
      open={isOpen}
      onOpenChange={
        // v8 ignore next
        (open) => {
          if (!open) onClose();
        }
      }
    >
      <Dialog.Portal>
        <Dialog.Overlay
          css={css`
            position: fixed;
            inset: 0;
            background: ${theme.colors.overlay};
            z-index: 50;
          `}
        />
        <Dialog.Content
          css={css`
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            background: ${theme.colors.surface};
            border: 1px solid ${theme.colors.border};
            border-radius: ${radii.lg};
            box-shadow: ${shadows.prominent};
            padding: ${spacing.xl};
            width: 90vw;
            max-width: 420px;
            z-index: 51;
            display: flex;
            flex-direction: column;
            gap: ${spacing.md};
          `}
        >
          <Dialog.Title
            css={css`
              margin: 0;
              font-size: ${fontSizes.xl};
              color: ${theme.colors.text};
              font-weight: 700;
            `}
          >
            Change Plan
          </Dialog.Title>

          <Dialog.Description
            css={css`
              margin: 0;
              font-size: ${fontSizes.sm};
              color: ${theme.colors.textSecondary};
            `}
          >
            {userEmail}
          </Dialog.Description>

          <select
            role="combobox"
            value={selectedPlanId}
            onChange={(e) => setSelectedPlanId(e.target.value)}
            css={css`
              padding: ${spacing.sm} ${spacing.md};
              border: 1px solid ${theme.colors.border};
              border-radius: ${radii.sm};
              background: ${theme.colors.background};
              color: ${theme.colors.text};
              font-size: ${fontSizes.md};
            `}
          >
            <option value="">Select plan</option>
            {plans.map((plan) => (
              <option key={plan.id} value={plan.id}>
                {plan.displayName}
              </option>
            ))}
          </select>

          {error && (
            <p
              css={css`
                margin: 0;
                color: ${theme.colors.error};
                font-size: ${fontSizes.sm};
              `}
            >
              {error}
            </p>
          )}

          <div
            css={css`
              display: flex;
              gap: ${spacing.sm};
              justify-content: flex-end;
            `}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              css={css`
                padding: ${spacing.sm} ${spacing.md};
                border: 1px solid ${theme.colors.border};
                border-radius: ${radii.sm};
                background: ${theme.colors.surface};
                color: ${theme.colors.text};
                cursor: pointer;
                font-size: ${fontSizes.sm};
                &:disabled {
                  opacity: 0.5;
                  cursor: not-allowed;
                }
              `}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!selectedPlanId || saving}
              css={css`
                display: flex;
                align-items: center;
                gap: ${spacing.xs};
                padding: ${spacing.sm} ${spacing.md};
                border: none;
                border-radius: ${radii.sm};
                background: ${theme.colors.primary};
                color: ${theme.colors.primaryText};
                cursor: pointer;
                font-size: ${fontSizes.sm};
                font-weight: 600;
                &:disabled {
                  opacity: 0.5;
                  cursor: not-allowed;
                }
              `}
            >
              {saving && <Spinner size="sm" aria-hidden />}
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
